/**
 * BookingStatusPage — public status lookup for consultation bookings.
 *
 * Route:
 *   /booking/status
 *   /booking/status/:ref   (ref auto-fills the input)
 *
 * The backend requires BOTH the short reference and the email that
 * placed the booking, so we always show both inputs and never expose
 * any booking data without proof of ownership.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MagnifyingGlass,
  ArrowLeft,
  CheckCircle,
  Clock,
  CalendarBlank,
  CurrencyCircleDollar,
  Hourglass,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  new: { text: 'قيد المراجعة', color: 'bg-[#EEF3E4] text-[#4A641F]' },
  scheduled: { text: 'مجدولة', color: 'bg-[#E1EDF3] text-[#265266]' },
  completed: { text: 'مكتملة', color: 'bg-[#E9F0DA] text-[#4A641F]' },
  cancelled: { text: 'ملغاة', color: 'bg-[#F5E7E4] text-[#B24A2C]' },
  rescheduled: { text: 'أعيد جدولتها', color: 'bg-[#F5EFDC] text-[#7A5C2B]' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ar-SA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const BookingStatusPage = () => {
  const { ref: refFromUrl } = useParams();
  const [reference, setReference] = useState((refFromUrl || '').toUpperCase());
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);

  // If a reference is present in the URL, focus the email input.
  useEffect(() => {
    if (refFromUrl) setReference(refFromUrl.toUpperCase());
  }, [refFromUrl]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!reference.trim() || !email.trim()) {
      toast.error('يرجى إدخال رقم الطلب والبريد الإلكتروني');
      return;
    }
    setLoading(true);
    setBooking(null);
    try {
      const res = await fetch(`${API_URL}/api/consultation-bookings/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reference: reference.trim().toUpperCase(),
          email: email.trim().toLowerCase(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data.detail || 'لم نعثر على حجز مطابق. تحقق من رقم الطلب والبريد',
        );
      }
      const data = await res.json();
      setBooking(data);
      toast.success('تم العثور على الحجز');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const status = booking
    ? STATUS_LABEL[booking.status] || STATUS_LABEL.new
    : null;

  return (
    <main
      data-testid="booking-status-page"
      className="pt-24 pb-20 min-h-screen bg-[#FAFAF7]"
      dir="rtl"
    >
      {/* Hero */}
      <section className="bg-[#1F3D2B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-xs sm:text-sm text-[#D8E0C9] hover:text-white mb-5 transition-colors"
            >
              ← العودة للصفحة الرئيسية
            </Link>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/30 text-[#D8E0C9] text-xs sm:text-sm mb-5">
              <Hourglass size={16} weight="duotone" />
              متابعة حجز جلسة
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold leading-tight font-['IBM_Plex_Sans_Arabic'] mb-4">
              حالة حجزك
            </h1>
            <p className="text-sm text-[#D8E0C9] leading-9 max-w-2xl">
              أدخل رقم الطلب والبريد الإلكتروني الذي أرسلنا عليه رسالة التأكيد،
              وسنعرض لك تفاصيل الحجز الحالية ورابط إعادة الجدولة إن كان متاحًا.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lookup form */}
      <section className="py-10 sm:py-14">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-[24px] border border-[#E6E0D6] p-6 sm:p-8 shadow-sm">
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div>
                <label className="text-sm font-medium text-[#1F3D2B] block mb-1.5">
                  رقم الطلب
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) =>
                    setReference(e.target.value.toUpperCase())
                  }
                  data-testid="lookup-reference-input"
                  placeholder="مثال: A1B2C3D4"
                  dir="ltr"
                  className="w-full bg-[#FAFAF7] border border-[#D8D2C8] rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:border-[#4A641F]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1F3D2B] block mb-1.5">
                  البريد الإلكتروني
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="lookup-email-input"
                  placeholder="البريد الذي استخدمته عند الحجز"
                  dir="ltr"
                  className="w-full bg-[#FAFAF7] border border-[#D8D2C8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#4A641F]"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="lookup-submit-btn"
                className="w-full bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-6 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                <MagnifyingGlass size={16} weight="bold" />
                {loading ? 'جارٍ البحث…' : 'عرض حالة الحجز'}
              </button>
            </form>
          </div>

          {/* Result */}
          {booking && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              data-testid="booking-result"
              className="mt-6 bg-white rounded-[24px] border border-[#E6E0D6] p-6 sm:p-8 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <p className="text-xs text-[#92A082] mb-1">
                    رقم الطلب
                  </p>
                  <p
                    className="text-lg font-bold font-mono text-[#1F3D2B]"
                    dir="ltr"
                  >
                    {booking.reference}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${status?.color}`}
                >
                  <CheckCircle size={14} weight="fill" />
                  {status?.text}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <InfoRow
                  icon={CalendarBlank}
                  label="نوع الجلسة"
                  value={booking.consultation_label_ar}
                />
                <InfoRow
                  icon={Clock}
                  label="المدة"
                  value={
                    booking.duration_min
                      ? `${booking.duration_min} دقيقة`
                      : '—'
                  }
                />
                <InfoRow
                  icon={CalendarBlank}
                  label="التاريخ المفضل"
                  value={booking.preferred_date || '—'}
                />
                <InfoRow
                  icon={Clock}
                  label="الوقت المفضل"
                  value={booking.preferred_time || '—'}
                />
                <InfoRow
                  icon={CurrencyCircleDollar}
                  label="القيمة"
                  value={
                    booking.price_sar
                      ? `${Number(booking.price_sar).toLocaleString()} ر.س`
                      : '—'
                  }
                />
                <InfoRow
                  icon={Clock}
                  label="أُنشئ بتاريخ"
                  value={fmtDate(booking.created_at)}
                />
                {booking.rescheduled_at && (
                  <InfoRow
                    icon={Hourglass}
                    label="آخر إعادة جدولة"
                    value={fmtDate(booking.rescheduled_at)}
                    span
                  />
                )}
              </div>

              {/* Reschedule note (public status does NOT expose the private
                  reschedule token; the client received it in the email
                  confirmation. We only guide them to that email). */}
              <div className="mt-6 pt-5 border-t border-[#EFEAE1]">
                {booking.status === 'cancelled' ||
                booking.status === 'completed' ? (
                  <p className="text-xs text-[#92A082] leading-7">
                    هذا الحجز مغلق ولا يمكن إعادة جدولته. لأي استفسار،
                    يرجى التواصل معنا عبر صفحة{' '}
                    <Link
                      to="/contact"
                      className="text-[#4A641F] hover:underline"
                    >
                      «اتصل بنا»
                    </Link>
                    .
                  </p>
                ) : (
                  <p className="text-xs text-[#92A082] leading-7">
                    لإعادة جدولة الحجز، افتح رسالة تأكيد الحجز التي
                    وصلتك على بريدك واستخدم رابط «إعادة الجدولة» فيها.
                    إن لم تجدها، تواصل معنا عبر{' '}
                    <Link
                      to="/contact"
                      className="text-[#4A641F] hover:underline"
                    >
                      «اتصل بنا»
                    </Link>
                    .
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </main>
  );
};

const InfoRow = ({ icon: Icon, label, value, span }) => (
  <div
    className={`flex items-start gap-3 bg-[#FAFAF7] border border-[#EFEAE1] rounded-xl p-3.5 ${
      span ? 'sm:col-span-2' : ''
    }`}
  >
    <div className="w-9 h-9 rounded-lg bg-[#F5F2EC] flex items-center justify-center shrink-0">
      <Icon size={18} weight="duotone" className="text-[#4A641F]" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] text-[#92A082] mb-0.5">{label}</p>
      <p className="text-sm text-[#1F3D2B] break-words">{value}</p>
    </div>
  </div>
);

export default BookingStatusPage;
